import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ApprovalRequests() {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchApprovalRequests();
    }
  }, [user]);

  const fetchApprovalRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/approval-requests`, {
        params: { status: 'pending' }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching approval requests:', error);
      toast.error('Onay istekleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setProcessing(true);
      await axios.patch(`${API}/approval-requests/${requestId}/approve`);
      toast.success('İstek onaylandı ve işlem gerçekleştirildi');
      setShowDetailDialog(false);
      fetchApprovalRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (requestId) => {
    try {
      setProcessing(true);
      await axios.patch(`${API}/approval-requests/${requestId}/reject`);
      toast.success('İstek reddedildi');
      setShowDetailDialog(false);
      fetchApprovalRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    } finally {
      setProcessing(false);
    }
  };

  const openDetailDialog = (request) => {
    setSelectedRequest(request);
    setShowDetailDialog(true);
  };

  const getTypeLabel = (type) => {
    return type === 'edit' ? 'Düzenleme' : 'Silme';
  };

  const getEntityLabel = (entity) => {
    const labels = {
      project: 'Proje',
      team: 'Takım',
      workflow: 'İş Akışı',
      task: 'Görev'
    };
    return labels[entity] || entity;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="approval-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              Bekleyen Onaylar
              {/* Onay talebi sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {requests.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Düzenleme ve silme işlemleri için bekleyen onay istekleri
            {requests.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {requests.length.toLocaleString('tr-TR')} bekleyen istek</span>
            )}
          </p>
        </div>
        
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-blue-200 rounded-xl px-4 py-2">
          <p className="text-sm text-gray-600 mb-1">Bekleyen İstek</p>
          <p className="text-2xl font-bold text-blue-600">{requests.length}</p>
        </div>
      </div>

      {/* Empty State */}
      {requests.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Bekleyen onay isteği bulunmuyor</h3>
          <p className="text-gray-600 mb-6">Yeni onay istekleri burada görünecektir</p>
        </div>
      ) : (
        /* Requests Grid */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requests.map((request) => (
            <div
              key={request.id}
              onClick={() => openDetailDialog(request)}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-blue-200 cursor-pointer"
              data-testid={`approval-request-${request.id}`}
            >
              <div className="p-6">
                {/* Header with Badge */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-12 h-12 ${request.type === 'delete' ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-cyan-500 to-blue-500'} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                      {request.type === 'delete' ? (
                        <XCircle className="w-6 h-6 text-white" />
                      ) : (
                        <FileText className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {request.entityName}
                      </h3>
                      <p className="text-sm text-gray-600">{getEntityLabel(request.entity)}</p>
                    </div>
                  </div>
                  
                  <span className={`px-3 py-1 rounded-md text-xs font-semibold ${
                    request.type === 'delete' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {getTypeLabel(request.type)}
                  </span>
                </div>

                {/* Info */}
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    <span className="font-medium">İsteyen:</span>
                    <span className="truncate">{request.requestedByName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatDate(request.createdAt)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(request.id);
                    }}
                    disabled={processing}
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Onayla
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReject(request.id);
                    }}
                    disabled={processing}
                    className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reddet
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              <div className={`w-10 h-10 ${selectedRequest?.type === 'delete' ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-cyan-500 to-blue-500'} rounded-lg flex items-center justify-center`}>
                {selectedRequest?.type === 'delete' ? (
                  <XCircle className="w-5 h-5 text-white" />
                ) : (
                  <FileText className="w-5 h-5 text-white" />
                )}
              </div>
              {selectedRequest?.entityName}
            </DialogTitle>
            <p className="text-gray-600 mt-2">
              {getEntityLabel(selectedRequest?.entity)} - {getTypeLabel(selectedRequest?.type)} İsteği
            </p>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6 mt-4">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">İsteyen</p>
                  <p className="text-gray-900">{selectedRequest.requestedByName}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">İstek Tarihi</p>
                  <p className="text-gray-900">{formatDate(selectedRequest.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">İşlem Tipi</p>
                  <span className={`inline-flex px-3 py-1 rounded-md text-sm font-semibold ${
                    selectedRequest.type === 'delete' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {getTypeLabel(selectedRequest.type)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Entity Tipi</p>
                  <p className="text-gray-900">{getEntityLabel(selectedRequest.entity)}</p>
                </div>
              </div>

              {/* Edit Details */}
              {selectedRequest.type === 'edit' && selectedRequest.requestDetails && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600" />
                    Değiştirilecek Alanlar
                  </p>
                  <div className="space-y-2">
                    {Object.entries(selectedRequest.requestDetails).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2 text-sm">
                        <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5"></span>
                        <div className="flex-1">
                          <span className="font-medium text-gray-700">{key}:</span>
                          <span className="text-gray-900 ml-2">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Warning */}
              {selectedRequest.type === 'delete' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Uyarı:</strong> Bu işlem geri alınamaz. {getEntityLabel(selectedRequest.entity)} kalıcı olarak silinecektir.
                    </span>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => handleApprove(selectedRequest.id)}
                  disabled={processing}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                >
                  {processing ? 'İşleniyor...' : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Onayla
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReject(selectedRequest.id)}
                  disabled={processing}
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reddet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
